import type { BoatDef } from './levels';
import { clamp } from './rng';

/**
 * Boats come in two families.
 *
 * Rafts are rows of buoyant deck panels with a pontoon float under each panel. Blow a panel away
 * and there is a hole in the deck; wear a float down and the raft loses lift on that side: it lists
 * toward the weak end and sits lower, and whoever is standing on the deck slides downhill unless
 * a stop at the deck's end catches them. A float that is only damaged still floats, just less.
 * Lose (nearly) all the lift and the raft goes under. A big raft can carry a raised second level
 * (a "castle") over some of its bays, reached by ramps at its ends.
 *
 * Vessels (yacht, fishing boat, police boat) are one body with one pool of hit points. They show
 * wear as they are hurt, smoke and then fire when nearly gone, and blow up and sink the moment the
 * last point is lost. A yacht has a second level of its own.
 */

export const HULL = 44;
export const STOP_H = 24;
export const PANEL_HP = 100;
/** Hit points of a raft's pontoon under each panel. Damaged floats lift less. */
export const FLOAT_HP = 100;
/** Floats are a little tougher than the deck over them. */
export const FLOAT_TOUGHNESS = 0.8;
/** A raft with no more than this fraction of its lift left goes under. */
export const SINK_AT = 0.12;
/** Radians of list when all the buoyancy is at one end. */
export const MAX_TILT = 0.4;
/** Decks steeper than this make moles slide. */
export const SLIDE_ANGLE = 0.14;
/** How far a fully stripped boat drops (it drowns everyone aboard). */
export const SUNK_DROP = 400;
/** How high a second level stands over the deck, and how thick its slab is under the walking line. */
export const UPPER_H = 76;
/** Solid thickness under a vessel's deck. */
export const VESSEL_HULL = 40;

export type BoatKind = 'raft' | 'yacht' | 'fishing' | 'police';
export const VESSEL_HP: Record<Exclude<BoatKind, 'raft'>, number> = { yacht: 320, fishing: 240, police: 220 };
export const VESSEL_NAMES: Record<Exclude<BoatKind, 'raft'>, string> = { yacht: 'The yacht', fishing: 'The trawler', police: 'The police boat' };

export function isVessel(b: { kind: BoatKind }): boolean {
  return b.kind !== 'raft';
}

export interface BoatPanel {
  hp: number;
  alive: boolean;
  /** Pontoon health (rafts): lift scales with it. */
  float: number;
  /** A ramp panel of a second level: -1 has its low end on the left, 1 on the right, 0 is flat. */
  ramp: -1 | 0 | 1;
}

export interface Boat {
  id: string;
  kind: BoatKind;
  /** Centre of the boat and the deck's resting waterline y. */
  x: number;
  y: number;
  panelW: number;
  panels: BoatPanel[];
  stops: BoatDef['stops'];
  /** Solid thickness under the walking line. */
  hull: number;
  /** Total hit points of a vessel's body (and the reference for its wear). */
  maxHp: number;
  /** A second level follows the boat it stands on. */
  parentId: string | null;
  /** The second level of a vessel is part of the body: it only goes when the vessel does. */
  armored: boolean;
  angle: number;
  sink: number;
  angleTarget: number;
  sinkTarget: number;
  sunk: boolean;
  /** Set once the "listing" callout has been shown. */
  listed: boolean;
}

export interface BoatSnap {
  id: string;
  panels: boolean[];
  /** Panel and float health, whole numbers; older snapshots lack them. */
  hp?: number[];
  floats?: number[];
  angle: number;
  sink: number;
}

export function boatFromDef(d: BoatDef): Boat {
  const kind: BoatKind = d.kind ?? 'raft';
  const vessel = kind !== 'raft';
  const bodyHp = vessel ? VESSEL_HP[kind as Exclude<BoatKind, 'raft'>] : PANEL_HP;
  return {
    id: d.id, kind, x: d.x, y: d.y, panelW: d.panelW, stops: d.stops,
    panels: Array.from({ length: vessel ? 1 : d.panels }, () => ({ hp: bodyHp, alive: true, float: FLOAT_HP, ramp: 0 as const })),
    hull: vessel ? VESSEL_HULL : HULL, maxHp: bodyHp, parentId: null, armored: false,
    angle: 0, sink: 0, angleTarget: 0, sinkTarget: 0, sunk: false, listed: false,
  };
}

/** The second level of `main`: bays of the raised deck with a ramp panel at each end that has one. */
function castleFor(main: Boat, d: BoatDef): Boat {
  const up = d.upper!;
  const ramps = up.ramps ?? 'both';
  const panels: BoatPanel[] = Array.from({ length: up.bays }, (_, i) => ({
    hp: PANEL_HP, alive: true, float: FLOAT_HP,
    ramp: (i === 0 && (ramps === 'both' || ramps === 'left') ? -1 : i === up.bays - 1 && (ramps === 'both' || ramps === 'right') ? 1 : 0) as -1 | 0 | 1,
  }));
  return {
    id: `${d.id}:upper`, kind: main.kind, x: d.x + up.at, y: d.y - UPPER_H, panelW: up.bayW, stops: 'none', panels,
    hull: UPPER_H + 2, maxHp: PANEL_HP, parentId: main.id, armored: main.kind !== 'raft',
    angle: 0, sink: 0, angleTarget: 0, sinkTarget: 0, sunk: false, listed: false,
  };
}

/** Every boat a level has, each raft or vessel followed by its second level when it has one. */
export function buildBoats(defs: readonly BoatDef[]): Boat[] {
  const out: Boat[] = [];
  for (const d of defs) {
    const main = boatFromDef(d);
    out.push(main);
    if (d.upper) out.push(castleFor(main, d));
  }
  return out;
}

export function boatLength(b: Boat): number {
  return b.panels.length * b.panelW;
}

/** Index of the panel under world x, intact or not; -1 outside the hull. */
export function panelIndexAt(b: Boat, x: number): number {
  const i = Math.floor((x - b.x + boatLength(b) / 2) / b.panelW);
  return i >= 0 && i < b.panels.length ? i : -1;
}

export function panelCenterX(b: Boat, i: number): number {
  return b.x - boatLength(b) / 2 + (i + 0.5) * b.panelW;
}

/** Deck surface y at world x, or null where no intact panel supports it. Ramp panels slope down to the deck below. */
export function deckY(b: Boat, x: number): number | null {
  if (b.sunk) return null;
  const i = panelIndexAt(b, x);
  if (i < 0 || !b.panels[i]!.alive) return null;
  let y = b.y + b.sink + (x - b.x) * Math.tan(b.angle);
  const ramp = b.panels[i]!.ramp;
  if (ramp !== 0) {
    const left = b.x - boatLength(b) / 2 + i * b.panelW;
    const f = clamp((x - left) / b.panelW, 0, 1);
    // ramp -1: low end on the left (f = 0 is low); ramp 1: low end on the right.
    y += (ramp === -1 ? 1 - f : f) * UPPER_H;
  }
  return y;
}

/** Solid hull under an intact panel: from the deck down `hull` px. */
export function boatContains(b: Boat, x: number, y: number): boolean {
  const d = deckY(b, x);
  return d !== null && y >= d && y <= d + b.hull;
}

/** Outer edges of the intact deck. */
export function deckExtent(b: Boat): { left: number; right: number } | null {
  let first = -1;
  let last = -1;
  b.panels.forEach((p, i) => {
    if (!p.alive) return;
    if (first < 0) first = i;
    last = i;
  });
  if (first < 0) return null;
  const x0 = b.x - boatLength(b) / 2;
  return { left: x0 + first * b.panelW, right: x0 + (last + 1) * b.panelW };
}

/** World x of the stop post on a side, if that side has one and its end panel still exists. */
export function stopX(b: Boat, side: 'left' | 'right'): number | null {
  if (b.stops === 'none' || (b.stops !== 'both' && b.stops !== side)) return null;
  const ext = deckExtent(b);
  if (!ext) return null;
  // A stop belongs to the boat's original end panel; it goes when that panel goes.
  const endIndex = side === 'left' ? 0 : b.panels.length - 1;
  if (!b.panels[endIndex]!.alive) return null;
  return side === 'left' ? ext.left + 4 : ext.right - 4;
}

/** True if moving from x to nx along the deck would pass a stop. */
export function crossesStop(b: Boat, x: number, nx: number): boolean {
  const l = stopX(b, 'left');
  const r = stopX(b, 'right');
  if (l !== null && x >= l && nx < l) return true;
  if (r !== null && x <= r && nx > r) return true;
  return false;
}

/** Fraction of a raft's lift that is left, 0..1 (floats count by health). */
export function liftLeft(b: Boat): number {
  if (b.panels.length === 0) return 0;
  return b.panels.reduce((s, p) => s + clamp(p.float / FLOAT_HP, 0, 1), 0) / b.panels.length;
}

/** A vessel's remaining body health, 0..1. */
export function bodyLeft(b: Boat): number {
  return clamp((b.panels[0]?.hp ?? 0) / b.maxHp, 0, 1);
}

export function recomputeTargets(b: Boat): void {
  if (b.parentId) return; // a second level follows its boat
  if (isVessel(b)) {
    if (!b.panels[0]!.alive) {
      b.sunk = true;
      b.sinkTarget = SUNK_DROP;
      // Goes down by the head or the stern, whichever way it was already leaning.
      b.angleTarget = b.angle >= 0 ? 0.32 : -0.32;
    } else {
      // Wear sits it a little lower.
      b.sinkTarget = (1 - bodyLeft(b)) * 14;
    }
    return;
  }
  const alive = b.panels.some((p) => p.alive);
  const lift = liftLeft(b);
  if (!alive || lift <= SINK_AT) {
    b.sunk = true;
    b.sinkTarget = SUNK_DROP;
    return;
  }
  const half = boatLength(b) / 2;
  const total = b.panels.reduce((s, p) => s + clamp(p.float / FLOAT_HP, 0, 1), 0);
  const cob = b.panels.reduce((s, p, i) => s + clamp(p.float / FLOAT_HP, 0, 1) * (panelCenterX(b, i) - b.x), 0) / total;
  // Buoyancy on the right means the left end drops: y grows toward negative local x.
  b.angleTarget = -MAX_TILT * clamp(cob / half, -1, 1);
  const lost = 1 - lift;
  b.sinkTarget = lost * 34 + Math.max(0, lost - 0.55) * 120;
}

export interface BoatHit {
  /** Deck panels (or the vessel body: index 0) that broke this time. */
  broke: number[];
  /** Raft floats that gave out entirely this time. */
  floatsBroke: number[];
  /** Whether anything was damaged at all (wear, a float losing lift). */
  touched: boolean;
  /** This hit sank the boat. */
  sank: boolean;
}

/** How far a point is outside a panel's box, in px (0 inside), measured to the walking line down `depth` px. */
function boxDistance(b: Boat, i: number, x: number, y: number, top: number, depth: number): number {
  const left = b.x - boatLength(b) / 2 + i * b.panelW;
  const dx = Math.max(0, Math.max(left - x, x - (left + b.panelW)));
  const cx = clamp(x, left, left + b.panelW);
  const line = (deckY(b, cx) ?? b.y + b.sink + (cx - b.x) * Math.tan(b.angle)) + top;
  const dy = Math.max(0, Math.max(line - y, y - (line + depth)));
  return Math.hypot(dx, dy);
}

/**
 * A blast (or a bullet) of `radius` and `dmg` at (x, y). Hurts every deck panel in reach, and on
 * a raft the float under each panel too, a little less. Vessels take it on their single body.
 */
export function hitBoat(b: Boat, x: number, y: number, radius: number, dmg: number): BoatHit {
  const out: BoatHit = { broke: [], floatsBroke: [], touched: false, sank: false };
  if (b.sunk || b.armored) return out;
  b.panels.forEach((p, i) => {
    if (!p.alive && p.float <= 0) return;
    if (p.alive) {
      // Full damage anywhere on the panel itself, falling off across the blast radius beyond it.
      const d = boxDistance(b, i, x, y, 0, b.hull);
      if (d <= radius) {
        p.hp -= dmg * clamp(1 - d / Math.max(1, radius), 0.25, 1);
        out.touched = true;
        if (p.hp <= 0) {
          p.hp = 0;
          p.alive = false;
          out.broke.push(i);
        }
      }
    }
    if (b.kind === 'raft' && !b.parentId && p.float > 0) {
      const fd = boxDistance(b, i, x, y, b.hull - 16, 20);
      if (fd <= radius) {
        p.float -= dmg * FLOAT_TOUGHNESS * clamp(1 - fd / Math.max(1, radius), 0.25, 1);
        out.touched = true;
        if (p.float <= 0) {
          p.float = 0;
          out.floatsBroke.push(i);
        }
      }
    }
  });
  if (out.touched) {
    const was = b.sunk;
    recomputeTargets(b);
    out.sank = !was && b.sunk;
  }
  return out;
}

/** Blast damage to panels near (x, y). Returns the indices that broke this time. */
export function damageBoat(b: Boat, x: number, y: number, radius: number, dmg: number): number[] {
  return hitBoat(b, x, y, radius, dmg).broke;
}

/** Whether the boat, or the second level standing on it, has anything to mend. */
export function needsRepair(boats: readonly Boat[], b: Boat): boolean {
  const root = b.parentId ?? b.id;
  return boats.some((x) => (x.id === root || x.parentId === root) && !x.sunk && (x.panels.some((p) => !p.alive || p.hp < x.maxHp) || (x.kind === 'raft' && x.panels.some((p) => p.float < FLOAT_HP))));
}

/** Mend a boat and its second level: every panel and float back to whole. Returns the panels rebuilt. */
export function repairBoat(boats: readonly Boat[], b: Boat): { boat: Boat; index: number }[] {
  const root = b.parentId ?? b.id;
  const rebuilt: { boat: Boat; index: number }[] = [];
  for (const x of boats) {
    if (x.id !== root && x.parentId !== root) continue;
    x.panels.forEach((p, i) => {
      if (!p.alive) rebuilt.push({ boat: x, index: i });
      p.alive = true;
      p.hp = x.maxHp;
      p.float = FLOAT_HP;
    });
    x.sunk = false;
    x.listed = false;
  }
  for (const x of boats) if (x.id === root) recomputeTargets(x);
  return rebuilt;
}

/** Ease toward the resting angle and depth. Returns true while still moving. */
export function updateBoat(b: Boat): boolean {
  if (b.parentId) return false;
  const da = b.angleTarget - b.angle;
  const ds = b.sinkTarget - b.sink;
  if (Math.abs(da) < 0.0005 && Math.abs(ds) < 0.05) {
    b.angle = b.angleTarget;
    b.sink = b.sinkTarget;
    return false;
  }
  b.angle += da * 0.035;
  b.sink += ds * (b.sunk ? 0.02 : 0.05);
  return true;
}

/** Second levels take their boat's angle and depth: same tilt, a fixed height above its deck. */
export function followParents(boats: readonly Boat[]): void {
  for (const c of boats) {
    if (!c.parentId) continue;
    const p = boats.find((x) => x.id === c.parentId);
    if (!p) continue;
    c.angle = p.angle;
    c.sink = p.sink + (c.x - p.x) * Math.tan(p.angle);
    c.sunk = p.sunk;
  }
}

/** Advance every boat one tick: the boats themselves, then the levels that ride them. */
export function updateBoats(boats: readonly Boat[]): void {
  for (const b of boats) updateBoat(b);
  followParents(boats);
}

export function boatSnap(b: Boat): BoatSnap {
  return {
    id: b.id, panels: b.panels.map((p) => p.alive), hp: b.panels.map((p) => Math.round(p.hp)), floats: b.panels.map((p) => Math.round(p.float)),
    angle: b.angle, sink: b.sink,
  };
}

export function applyBoatSnap(b: Boat, s: BoatSnap): void {
  s.panels.forEach((alive, i) => {
    const p = b.panels[i];
    if (!p) return;
    p.alive = alive;
    if (s.hp) p.hp = s.hp[i] ?? p.hp;
    else if (!alive) p.hp = 0;
    if (!alive) p.hp = 0;
    if (s.floats) p.float = s.floats[i] ?? p.float;
  });
  b.angle = s.angle;
  b.sink = s.sink;
  recomputeTargets(b);
  b.angle = s.angle;
  b.sink = s.sink;
}
